/** @file Feedback store module for retrieving and updating feedbacks with the server. */
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { array, assert, expr, map } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as models from '@/models';
import { SubmissionsStore } from '@/store';
import * as utils from '@/utils';
import { Counter } from '@/utils/counter';
const storeBuilder = getStoreBuilder();
/**
 * Function to get initial state of the loaders of the state.
 *
 * @returns The initial state of `state.loaders`.
 */
function createEmptyLoaders() {
    return {
        feedback: {},
        inlineFeedbackByUser: {},
    };
}
/**
 * Function to get initial state of the store module.
 *
 * @returns The initial state of the store module.
 */
function createState() {
    return {
        feedbacks: {},
        inlineFeedbackByUser: {},
        loaders: createEmptyLoaders(),
    };
}
const moduleBuilder = storeBuilder.module('feedback', createState());
/** The Feedback store module. */
export var FeedbackStore;
(function (FeedbackStore) {
    /* eslint-disable-next-line no-underscore-dangle, jsdoc/require-jsdoc -- When touching please add documentation. */
    function _getFeedback(state) {
        return (assignmentId, submissionId) => state.feedbacks?.[assignmentId]?.[submissionId];
    }
    const commitFeedbackLoader = moduleBuilder.commit((state, payload) => {
        payload.loader.caseOf({
            Just: loader => utils.vueSet(state.loaders.feedback, payload.submissionId, loader),
            Nothing: () => utils.vueDelete(state.loaders.feedback, payload.submissionId),
        });
    }, 'commitFeedbackLoader');
    const commitInlineFeedbackyByUserLoader = moduleBuilder.commit((state, payload) => {
        const { assignmentId, userId } = payload;
        const mapping = utils.vueSetDefault(state.loaders.inlineFeedbackByUser, assignmentId, {});
        payload.loader.caseOf({
            Just: loader => utils.vueSet(mapping, userId, loader),
            Nothing: () => utils.vueDelete(mapping, userId),
        });
    }, 'commitInlineFeedbackyByUserLoader');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.getFeedback = moduleBuilder.read(_getFeedback, 'getFeedback');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.getSubmissionWithFeedbackByUser = moduleBuilder.read(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    state => (assignmentId, userId) => Maybe.fromNullable(state.inlineFeedbackByUser[assignmentId]?.[userId]), 'getSubmissionWithFeedbackByUser');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.commitSetFeedback = moduleBuilder.commit((state, payload) => {
        const trees = state.feedbacks[payload.assignmentId] ?? {};
        utils.vueSet(trees, payload.submissionId, payload.feedback);
        utils.vueSet(state.feedbacks, payload.assignmentId, trees);
    }, 'commitSetFeedback');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.commitDeleteFeedback = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        utils.vueDelete(state.feedbacks, payload.assignmentId);
        utils.vueDelete(state.inlineFeedbackByUser, payload.assignmentId);
    }, 'commitDeleteFeedback');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.commitDeleteAllFeedback = moduleBuilder.commit(state => {
        Object.assign(state, createState());
    }, 'commitDeleteAllFeedback');
    /**
     * Commit an updated feedback thread to the store.
     *
     * @param state The current store state.
     * @param payload Data required to perform the update.
     */
    const commitUpdateFeedback = moduleBuilder.commit((state, payload) => {
        const { assignmentId, submissionId, feedback, authorId } = payload;
        utils.vueSet(state.feedbacks[assignmentId], submissionId, feedback);
        // If we know the author of the updated reply, we update the count
        // for that author in the `inlineFeedbackByUser` mapping.
        const byUserForAssig = state.inlineFeedbackByUser[assignmentId];
        const byAuthor = authorId == null ? null : byUserForAssig?.[authorId];
        if (authorId != null && byAuthor != null) {
            const updatedCount = expr.match(payload, 'updateType', {
                // If the reply was changed the count doesn't change.
                changed: () => Maybe.empty(),
                // Increment the count by 1 when a reply was added. If the submission was not
                // yet present in the mapping, we know at this point that the author did not
                // yet place a comment on this submission because the mapping for that author
                // was already present.
                added: () => Maybe.of(byAuthor.get(submissionId).orDefault(0) + 1),
                // Decrement the count by 1 when a reply was deleted.
                deleted: () => byAuthor.get(submissionId).map(count => count - 1),
            });
            updatedCount.ifJust(count => {
                const newForSubmission = new map.ReadonlyMaybeMap([
                    ...byAuthor.entries(),
                    [submissionId, count],
                ]);
                utils.vueSet(byUserForAssig, authorId, newForSubmission);
                utils.vueSet(state.inlineFeedbackByUser, assignmentId, byUserForAssig);
            });
        }
    }, 'commitUpdateFeedback');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const getCommentCountOfUser = (payload) => {
        const { userId, comments } = payload;
        const counter = Counter.make(comments, comment => comment.workId, comment => array.sumMap(comment.replies, reply => (reply.authorId === userId ? 1 : 0)));
        return counter.toMap();
    };
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FeedbackStore.commitSetFeedbackByUser = moduleBuilder.commit((state, payload) => {
        const { assignmentId, userId, mapping } = payload;
        if (state.inlineFeedbackByUser[assignmentId] == null) {
            utils.vueSet(state.inlineFeedbackByUser, assignmentId, {});
        }
        utils.vueSet(state.inlineFeedbackByUser[assignmentId], userId, mapping);
    }, 'commitSetFeedbackByUser');
    const commitClearFeedback = moduleBuilder.commit(state => {
        Object.assign(state, createState());
    }, 'commitClearFeedback');
    /** Action to load feedback of an assignment submission. */
    FeedbackStore.loadFeedback = moduleBuilder.dispatch(async ({ state }, { assignmentId, submissionId, force = false, }) => await utils.storeLoadWithMaybeForce({
        force,
        value: Maybe.fromNullable(FeedbackStore.getFeedback()(assignmentId, submissionId)),
        loader: Maybe.fromNullable(state.loaders.feedback[submissionId]),
        addLoader: loader => commitFeedbackLoader({ submissionId, loader: Maybe.of(loader) }),
        removeLoader: () => commitFeedbackLoader({ submissionId, loader: Maybe.empty() }),
        load: async () => {
            const [{ cgResult }] = await Promise.all([
                client.p.submission
                    .getFeedback({ submissionId, withReplies: true })
                    .catch(err => {
                    if (err instanceof api.ApiResultError &&
                        err.response.status === 403) {
                        return Promise.resolve(api.ApiResult.fromResponseAndValue(err.response, undefined));
                    }
                    return Promise.reject(err);
                }),
                SubmissionsStore.loadSingleSubmission({
                    assignmentId,
                    submissionId,
                }),
            ]);
            if (cgResult?.type === 'feedback-with-replies') {
                const feedback = models.Feedback.fromApiServerData(cgResult);
                FeedbackStore.commitSetFeedback({
                    assignmentId,
                    submissionId,
                    feedback,
                });
                return feedback;
            }
            assert.AssertionError.typeAssert(cgResult);
            throw new Error('Expected a FeedbackWithReplies');
        },
    }), 'loadFeedback');
    /** Action to create a new feedback thread. */
    FeedbackStore.createFeedbackThread = moduleBuilder.dispatch(async (_, payload) => {
        const { assignmentId, submissionId, userId, location } = payload;
        const requestBody = location ?? { workId: submissionId };
        const [response, feedback] = await Promise.all([
            client.p.comment.createBase({ requestBody }),
            FeedbackStore.loadFeedback({ assignmentId, submissionId }),
        ]);
        return response.map(cgResult => {
            const feedbackLine = models.FeedbackThread.fromApiServerData(cgResult).createReply(userId);
            commitUpdateFeedback({
                assignmentId,
                submissionId,
                feedback: feedback.updateThread(feedbackLine),
                authorId: userId,
                updateType: 'added',
            });
            return feedbackLine;
        });
    }, 'createFeedbackLline');
    /**
     * Update a feedback thread in the store after a comment in the thread was updated.
     *
     * Note: this will _not_ send any updates to the thread to the backend.
     */
    const updateThread = moduleBuilder.dispatch(async (_, payload) => {
        const { assignmentId, submissionId, thread } = payload;
        const response = await FeedbackStore.loadFeedback({ assignmentId, submissionId });
        commitUpdateFeedback({
            assignmentId,
            submissionId,
            feedback: response.updateThread(thread),
            authorId: payload.authorId,
            updateType: payload.updateType,
        });
        return response;
    }, 'updateThread');
    /**
     * Update an existing reply in a thread and commit it to the store.
     */
    FeedbackStore.updateReplyInThread = moduleBuilder.dispatch((_, payload) => updateThread({
        ...payload,
        thread: payload.thread.updateReply(payload.reply),
        authorId: payload.reply.authorId,
        updateType: 'changed',
    }), 'updateReplyInThread');
    /**
     * Commit a thread updated with a new reply to the store.
     *
     * This does not accept the added reply under a separate key in the
     * `payload` because the methods available on the `FeedbackThread` model to
     * add a new reply never give us the newly added reply.
     */
    FeedbackStore.addReplyToThread = moduleBuilder.dispatch((_, payload) => updateThread({
        ...payload,
        authorId: payload.authorId,
        updateType: 'added',
    }), 'addReplyToThread');
    /**
     * Delete a reply from a thread and commit the thread to the store.
     */
    FeedbackStore.deleteReplyFromThread = moduleBuilder.dispatch((_, payload) => updateThread({
        ...payload,
        thread: payload.thread.deleteReply(payload.reply),
        authorId: payload.reply.authorId,
        updateType: 'deleted',
    }), 'deleteReplyFromThread');
    /** Action to load inline feedbacks from a user. */
    FeedbackStore.loadInlineFeedbackByUser = moduleBuilder.dispatch(({ state }, payload) => {
        const { assignmentId, userId, force = false } = payload;
        return utils.loadApiWithMaybeForce({
            force,
            value: FeedbackStore.getSubmissionWithFeedbackByUser()(assignmentId, userId),
            loader: Maybe.fromNullable(state.loaders.inlineFeedbackByUser[assignmentId]?.[userId]),
            addLoader: loader => commitInlineFeedbackyByUserLoader({
                assignmentId,
                userId,
                loader: Maybe.of(loader),
            }),
            removeLoader: () => commitInlineFeedbackyByUserLoader({
                assignmentId,
                userId,
                loader: Maybe.empty(),
            }),
            load: async () => {
                const apiResult = await client.p.assignment.getCommentsByUser({
                    assignmentId,
                    userId,
                });
                const { cgResult } = apiResult;
                const comments = cgResult.map(models.FeedbackThread.fromApiServerData);
                const mapping = getCommentCountOfUser({ userId, comments });
                FeedbackStore.commitSetFeedbackByUser({
                    assignmentId,
                    userId,
                    mapping,
                });
                return apiResult.map(() => mapping);
            },
        });
    }, 'loadInlineFeedbackByUser');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        commitClearFeedback();
        FeedbackStore.commitDeleteAllFeedback();
    });
})(FeedbackStore || (FeedbackStore = {}));
